import { DropdownItemProps } from "semantic-ui-react";
import { ICountryNationalityProps } from "../store/nationalitiesReducer";
import { CertificateType } from "./certificate";
import { IExaminationOrganization } from "./examinationOrganization";
import { IPaymentType } from "./paymentType";

export interface IPaginationWrapper {
    totalRows: number;
    result: any[];
}

export const daysProps = [...new Array(31)].map((x, i) => ({
    key: i + 1,
    text: i + 1,
    value: i + 1,
}));

export const genderProps: DropdownItemProps[] = [
    { key: 0, text: "unspecified", value: 0 },
    { key: 1, text: "male", value: 1 },
    { key: 2, text: "female", value: 2 },
];

export const riverRangeProps: DropdownItemProps[] = [
    { key: 0, text: "D--2074-2081", value: 0 },
    { key: 1, text: "D--2001-2036", value: 1 },
    { key: 2, text: "D--1880-1920", value: 2 },
    { key: 3, text: "D--2249-2322", value: 3 },
    { key: 4, text: "D--1433-1708", value: 4 },
    { key: 5, text: "D--1708-1811", value: 5 },
    { key: 6, text: "EL-0003-0332", value: 6 },
    { key: 7, text: "EL-0333-0569", value: 7 },
    { key: 8, text: "EL-0573-0607", value: 8 },
    { key: 9, text: "RH-0352-0425", value: 9 },
    { key: 10, text: "RH-0425-0498", value: 10 },
    { key: 11, text: "RH-0498-0592", value: 11 },
    { key: 12, text: "RH-0592-0769", value: 12 },
    { key: 13, text: "RH-0769-0857", value: 13 },
    { key: 14, text: "UWE0000-0204", value: 14 },
].sort((a, b) => a.text.localeCompare(b.text));

const months = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
];

export const monthsProps = [...new Array(12)].map((x, i) => ({
    key: i + 1,
    text: months[i],
    value: i + 1,
}));

export const yearsProps = [...new Array(100)].fill(new Date().getFullYear() + 20).map((x, i) => ({
    key: x - i,
    text: x - i,
    value: x - i,
}));

export const yearsForEngineerProps = [...new Array(100)]
    .fill(new Date().getFullYear() + 77)
    .map((x, i) => ({
        key: x - i,
        text: x - i,
        value: x - i,
    }));

export const birthYearsProps = [...new Array(100)].fill(new Date().getFullYear()).map((x, i) => ({
    key: x - i,
    text: x - i,
    value: x - i,
}));

export const daysOfBirth: DropdownItemProps[] = [];

export const languageProps: DropdownItemProps[] = [
    { key: "de", text: "", value: "de", flag: "de" },
    { key: "en", text: "", value: "en", flag: "uk" },
];

export interface IDate {
    day: number;
    month: number;
    year: number;
}

export const dateInit: IDate = {
    day: -1,
    month: -1,
    year: -1,
};

export const examinationOrPaymentToDropdownProp = (
    items: IExaminationOrganization[] | IPaymentType[]
) => {
    return items
        .filter(x => !x.disabled)
        .map(x => {
            return {
                key: x.guid,
                text: x.name,
                value: x.guid,
            };
        });
};

export const constructDate = (date: IDate | Date): string => {
    if ("year" in date)
        return (
            (date.year === 1 ? "0001" : date.year) +
            "/" +
            date.month.toString().padStart(2, "0") +
            "/" +
            date.day.toString().padStart(2, "0")
        );
    else
        return (
            date.getFullYear() +
            "/" +
            (date.getMonth() + 1).toString().padStart(2, "0") +
            "/" +
            date.getDate().toString().padStart(2, "0")
        );
};

export const deconstructDate = (dateString: string): IDate => {
    const d = new Date(dateString);
    return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
};

export const getTodayDate = (): IDate => {
    const d = new Date();
    return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() };
};

export const getEmptyDate = (): IDate => {
    return { day: -1, month: -1, year: -1 };
};

const getAge = (dateOfBirth: string): number => {
    const d = new Date(dateOfBirth);
    const diff = new Date().getTime() - d.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
};

export const getCertificateEndDate = (dateOfBirth: string, type: CertificateType): IDate => {
    const age = getAge(dateOfBirth);
    const today = getTodayDate();
    switch (type) {
        case CertificateType.UcqBoatmaster:
            if (age < 47) return { ...today, year: today.year + 13 };
            else if (age >= 47 && age < 60) {
                const dob = deconstructDate(dateOfBirth);
                return { ...dob, year: dob.year + 60 };
            } else if (age >= 60 && age < 70) return { ...today, year: today.year + 5 };
            else if (age >= 70) return { ...today, year: today.year + 2 };
            return dateInit;
        case CertificateType.UcqLngExpert:
        case CertificateType.UcqPassengerNavigationExpert:
            return { ...today, year: today.year + 5 };
        default:
            return dateInit;
    }
};

export const compareDates = (d1: IDate, d2: IDate, allowSame = false): boolean | null => {
    if (
        Object.values(d1).some(x => x === -1 || x === "") ||
        Object.values(d2).some(x => x === -1 || x === "")
    )
        return null;
    const date1 = new Date(d1.year, d1.month - 1, d1.day);
    const date2 = new Date(d2.year, d2.month - 1, d2.day);
    if (allowSame) return date2 >= date1;
    else return date2 > date1;
};

export const formatDate = (dateString: string, includeTime: boolean = false) => {
    const isoDateFormat =
        /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]+)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?$/i;
    const isIsoDateString =
        dateString && typeof dateString === "string" && isoDateFormat.test(dateString);
    if (!isIsoDateString) return dateString;
    const date = new Date(dateString);
    const formattedDate =
        date.getDate().toString().padStart(2, "0") +
        "/" +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        "/" +
        date.getFullYear();
    if (includeTime)
        return (
            formattedDate +
            " " +
            date.getHours().toString().padStart(2, "0") +
            ":" +
            date.getMinutes().toString().padStart(2, "0") +
            ":" +
            date.getSeconds().toString().padStart(2, "0")
        );
    else return formattedDate;
};

export enum MessageStatus {
    Unprocessed,
    Success,
    Failure,
}

export const messageStatusProps: DropdownItemProps[] = [
    { key: MessageStatus.Unprocessed, text: "unprocessed", value: MessageStatus.Unprocessed },
    { key: MessageStatus.Success, text: "success", value: MessageStatus.Success },
    { key: MessageStatus.Failure, text: "failure", value: MessageStatus.Failure },
];

export const getCountryFromIssuingAuthority = (
    issuingAuthority: string,
    countryProps: ICountryNationalityProps[]
) => {
    if (!issuingAuthority) return -1;
    if (issuingAuthority.includes("AT"))
        return countryProps.find(x => (x.text as string) === "AUT")?.value as number;
    else if (issuingAuthority.includes("CH"))
        return countryProps.find(x => (x.text as string) === "CHE")?.value as number;
    else return -1;
};
